import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { onAuthStateChanged, type User } from "firebase/auth";
import { doc, serverTimestamp, setDoc } from "firebase/firestore";
import { COL_PIPELINE_USERS } from "@closing/shared";
import { auth, db, isAllowedEmail, signOut } from "./firebase";
import { api } from "./api";

type AuthState = {
  user: User | null;
  loading: boolean;
  domainOk: boolean;
  // Admin status from GET /api/me (ADMIN_EMAILS env). null until resolved:
  // UI affordances treat null as "not admin"; route guards treat it as
  // "still loading" so admins aren't bounced.
  isAdmin: boolean | null;
};

const Ctx = createContext<AuthState>({
  user: null,
  loading: true,
  domainOk: false,
  isAdmin: null,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Omit<AuthState, "isAdmin">>({
    user: null,
    loading: true,
    domainOk: false,
  });
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      const domainOk = isAllowedEmail(u?.email);
      if (u && !domainOk) {
        // Boot anyone whose email doesn't match an allowed domain.
        signOut();
      }
      setState({ user: u, loading: false, domainOk });
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!state.user || !state.domainOk) {
      setIsAdmin(null);
      return;
    }
    let cancelled = false;
    // One round-trip per login: learn admin status from the backend.
    api
      .me()
      .then(({ isAdmin }) => {
        if (!cancelled) setIsAdmin(isAdmin === true);
      })
      .catch(() => {
        if (!cancelled) setIsAdmin(false);
      });
    // Upsert our entry in the shared pipeline-users directory (same as the
    // CRM does) so assignee pickers and FCM notifications can find us.
    const u = state.user;
    setDoc(
      doc(db, COL_PIPELINE_USERS, u.uid),
      {
        email: u.email?.toLowerCase() ?? "",
        displayName: u.displayName ?? u.email ?? "",
        photoURL: u.photoURL ?? null,
        lastSeenAt: serverTimestamp(),
      },
      { merge: true },
    ).catch((err) => console.warn("[auth] pipeline-users upsert failed", err));
    return () => {
      cancelled = true;
    };
  }, [state.user, state.domainOk]);

  return <Ctx.Provider value={{ ...state, isAdmin }}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  return useContext(Ctx);
}
